import {
  Bedtime,
  DirectionsRun,
  MonitorHeart,
  MonitorWeight,
  PhoneAndroid,
} from "@mui/icons-material";
import { Box, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import type { WearableObservationSectionId } from "@/utils/wearableObservationFormat";
import { buildSectionView, type MetricRow } from "../observationView";
import type { AwakeningRecord } from "../observationNotes";
import { AwakeningRecordsBlock } from "./AwakeningRecordsBlock";

/** Ordem e identidade visual das seções da página. */
export const SECTION_META: {
  id: WearableObservationSectionId;
  title: string;
  Icon: React.ComponentType<SvgIconProps>;
}[] = [
  { id: "general", title: "Dados gerais", Icon: MonitorHeart },
  { id: "sleep", title: "Sono", Icon: Bedtime },
  { id: "bodyComposition", title: "Composição corporal", Icon: MonitorWeight },
  { id: "screenTime", title: "Tempo de tela", Icon: PhoneAndroid },
  { id: "exercise", title: "Atividade física", Icon: DirectionsRun },
];

/** Uma linha da lista: rótulo à esquerda, valor à direita. */
function MetricLine({ metric }: { metric: MetricRow }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{
        px: { xs: 1.5, sm: 2.5 },
        py: 1.5,
        minWidth: 0,
        borderBottom: "1px solid",
        borderColor: "neutral200.main",
      }}
    >
      {/* Nome do campo (Passos, Frequência cardíaca, etc.) */}
      <Tooltip title={metric.label} placement="top" arrow>
        <Typography
          sx={{
            color: "text.secondary",
            minWidth: 0,
            flex: 1,
            fontSize: "1.0625rem",
            fontWeight: 600,
            overflowWrap: "anywhere",
          }}
        >
          {metric.label}
        </Typography>
      </Tooltip>
      <Stack
        alignItems="flex-end"
        sx={{ minWidth: 0, maxWidth: { xs: "55%", sm: "none" } }}
      >
        <Typography
          sx={{
            fontSize: "1.125rem",
            fontWeight: 700,
            lineHeight: 1.3,
            textAlign: "right",
            overflowWrap: "anywhere",
          }}
        >
          {metric.value}
        </Typography>
        {metric.hint && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {metric.hint}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

/** Bloco de detalhe: título do item e seus valores em pares compactos. */
function DetailBlock({
  title,
  period,
  metrics,
}: {
  title: string;
  period?: string;
  metrics: MetricRow[];
}) {
  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2.5 },
        py: 2,
        minWidth: 0,
        borderBottom: "1px solid",
        borderColor: "neutral200.main",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="baseline"
        flexWrap="wrap"
        useFlexGap
        sx={{ minWidth: 0 }}
      >
        <Typography
          sx={{
            fontSize: "1.0625rem",
            fontWeight: 700,
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>
        {period && (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {period}
          </Typography>
        )}
      </Stack>

      <Box
        sx={{
          mt: 1.5,
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
        }}
      >
        {metrics.map((metric) => (
          <Stack key={metric.id} spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", overflowWrap: "anywhere" }}
            >
              {metric.label}
            </Typography>
            <Typography
              sx={{
                fontSize: "1.0625rem",
                fontWeight: 700,
                overflowWrap: "anywhere",
              }}
            >
              {metric.value}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}

interface ObservationSectionProps {
  title: string;
  Icon: React.ComponentType<SvgIconProps>;
  /** Nome do aparelho exibido uma única vez, no cabeçalho da seção. */
  deviceName?: string;
  items: any[];
  /** Somente Sono: despertares exibidos no fim da seção. */
  awakeningRecords?: AwakeningRecord[];
}

/** Seção em painel único: cabeçalho enxuto e medições em lista. */
export function ObservationSection({
  title,
  Icon,
  deviceName,
  items,
  awakeningRecords = [],
}: ObservationSectionProps) {
  const { metrics, details } = buildSectionView(items);

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "neutral300.main",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 1.5,
          minWidth: 0,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          borderBottom: "1px solid",
          borderColor: "neutral300.main",
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0, flex: 1 }}
        >
          <Icon sx={{ fontSize: 24, color: "primary800.main" }} aria-hidden />
          <Typography
            sx={{
              fontSize: "1.0625rem",
              fontWeight: 700,
              minWidth: 0,
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </Typography>
        </Stack>
        {deviceName && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              textAlign: "right",
              minWidth: 0,
              maxWidth: { xs: "45%", sm: "none" },
              overflowWrap: "anywhere",
            }}
          >
            {deviceName}
          </Typography>
        )}
      </Stack>

      {details.map((detail) => (
        <DetailBlock
          key={detail.id}
          title={detail.title}
          period={detail.period}
          metrics={detail.metrics}
        />
      ))}

      {/* Lista em colunas: cada medição ocupa uma única linha */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {metrics.map((metric) => (
          <MetricLine key={metric.id} metric={metric} />
        ))}
      </Box>

      {/* Despertares sempre por último, depois das medições do sono */}
      <AwakeningRecordsBlock records={awakeningRecords} />
    </Paper>
  );
}
